
import React from 'react';
import { useGlobalEnrichmentStatus } from '@/hooks/useGlobalEnrichmentStatus';
import { Loader2 } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

export const GlobalEnrichmentIndicator: React.FC = () => {
  const { data: ongoingEnrichments, isLoading, error } = useGlobalEnrichmentStatus(15000); // Poll every 15 seconds

  if (isLoading || error || !ongoingEnrichments || ongoingEnrichments.length === 0) {
    return null;
  }

  const count = ongoingEnrichments.length;
  const clientNames = ongoingEnrichments
    .map(run => run.clients?.name)
    .filter(Boolean)
    .slice(0, 3)
    .join(', ');
  const andMore = count > 3 ? ` and ${count - 3} more` : '';

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to="/clients"
            className={cn(
              "flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-colors",
              "bg-blue-500 text-blue-50 hover:bg-blue-600", // Adjusted colors for visibility
              "animate-pulse" // Adding a pulse animation for better visibility
            )}
            aria-label={`Enrichment in progress for ${count} clients`}
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span>{count} Processing</span>
          </Link>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs bg-background text-foreground border shadow-lg rounded-md p-3">
          <p className="font-semibold text-sm mb-1">Enrichment In Progress</p>
          <p className="text-xs text-muted-foreground">
            Currently enriching data for: {clientNames}{andMore}.
          </p>
          <p className="text-xs mt-2 opacity-80">Click to view all clients.</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
